import { create } from 'zustand';
import type { BdmInfo } from '@/types/quotation';
import { bdmService } from '@/services/bdmService';

interface DesignConsultantStore {
  // Backwards compatible (existing UI expects these)
  bdms: BdmInfo[];
  fetchBdms: () => Promise<void>;
  addBdm: (bdm: Omit<BdmInfo, 'id'>) => Promise<void>;
  updateBdm: (id: string, updates: Partial<BdmInfo>) => Promise<void>;
  deleteBdm: (id: string) => Promise<void>;

  // Preferred naming in UI
  designConsultants: BdmInfo[];
  fetchDesignConsultants: () => Promise<void>;
  addDesignConsultant: (consultant: Omit<BdmInfo, 'id'>) => Promise<void>;
  updateDesignConsultant: (id: string, updates: Partial<BdmInfo>) => Promise<void>;
  deleteDesignConsultant: (id: string) => Promise<void>;

  isLoading: boolean;
}

export const useDesignConsultantStore = create<DesignConsultantStore>((set) => ({
  bdms: [],
  designConsultants: [],
  isLoading: false,

  fetchBdms: async () => {
    set({ isLoading: true });
    try {
      const bdms = await bdmService.getBdms();
      set({ bdms, designConsultants: bdms, isLoading: false });
    } catch (error) {
      console.error('[BDM Store] Failed to fetch BDMs:', error);
      set({ isLoading: false });
    }
  },

  addBdm: async (bdm) => {
    set({ isLoading: true });
    try {
      const newBdm = await bdmService.addBdm(bdm);
      set((state) => {
        const next = [...state.bdms, newBdm];
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to add BDM:', error);
      set({ isLoading: false });
    }
  },

  updateBdm: async (id, updates) => {
    set({ isLoading: true });
    try {
      const updated = await bdmService.updateBdm(id, updates);
      set((state) => {
        const next = state.bdms.map((b) => (b.id === id ? updated : b));
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to update BDM:', error);
      set({ isLoading: false });
    }
  },

  deleteBdm: async (id) => {
    set({ isLoading: true });
    try {
      await bdmService.deleteBdm(id);
      set((state) => {
        const next = state.bdms.filter((b) => b.id !== id);
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to delete BDM:', error);
      set({ isLoading: false });
    }
  },

  fetchDesignConsultants: async () => {
    set({ isLoading: true });
    try {
      const designConsultants = await bdmService.getBdms();
      set({ bdms: designConsultants, designConsultants, isLoading: false });
    } catch (error) {
      console.error('[BDM Store] Failed to fetch design consultants:', error);
      set({ isLoading: false });
    }
  },

  addDesignConsultant: async (consultant) => {
    set({ isLoading: true });
    try {
      const newDesignConsultant = await bdmService.addBdm(consultant);
      set((state) => {
        const next = [...state.designConsultants, newDesignConsultant];
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to add design consultant:', error);
      set({ isLoading: false });
    }
  },

  updateDesignConsultant: async (id, updates) => {
    set({ isLoading: true });
    try {
      const updated = await bdmService.updateBdm(id, updates);
      set((state) => {
        const next = state.designConsultants.map((c) => (c.id === id ? updated : c));
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to update design consultant:', error);
      set({ isLoading: false });
    }
  },

  deleteDesignConsultant: async (id) => {
    set({ isLoading: true });
    try {
      await bdmService.deleteBdm(id);
      set((state) => {
        const next = state.designConsultants.filter((c) => c.id !== id);
        return { bdms: next, designConsultants: next, isLoading: false };
      });
    } catch (error) {
      console.error('Failed to delete design consultant:', error);
      set({ isLoading: false });
    }
  }
}));

// Backwards-compatible alias (existing code still imports/use this)
export const useBdmStore = useDesignConsultantStore;
